import {
  UseMutationOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { IEvent } from "./types"
import api from "services/api"

export function useEvents(
  {
    eventName,
    start_date,
    status,
  }: {
    eventName?: string | null
    status?: string | null
    start_date?: { operator: string; values: (string | null)[] } | null
  },
  config?: UseQueryOptions<any>
) {
  const filters: any = {}
  if (eventName) filters.name = { operator: "contains", values: [eventName] }
  if (status) filters.status = { operator: "in", values: [status] }
  if (start_date)
    filters.start_date = {
      operator: start_date.operator,
      values:
        start_date.operator !== "between"
          ? start_date.values
          : [start_date.values],
    }
  return useInfiniteQuery<IPaginatedResponse<IEvent[]>>(
    [
      "operation",
      "production",
      "events",
      { scope: "from_event_app", eventName },
    ],
    async ({ pageParam = 1 }) =>
      api
        .get(`operation/production/events`, {
          params: {
            scope: "from_event_app",
            per_page: 10,
            page: pageParam,
            paginated: true,
            filter: Object.keys(filters).length > 0 ? filters : undefined,
          },
        })
        .then(({ data }) => data),
    {
      refetchOnWindowFocus: false,
      getNextPageParam: ({ meta }) => {
        return meta?.last_page > meta?.current_page && meta?.current_page + 1
      },
      ...config,
    }
  )
}

export function useEvent(eventId: string, config?: UseQueryOptions<IEvent>) {
  return useQuery<IEvent>(["operation", "production", "events", eventId], {
    ...config,
  })
}

export function useTerminateEvent(
  eventId: string,
  config?: UseMutationOptions
) {
  const queryClient = useQueryClient()

  return useMutation(
    (data) =>
      api.post(
        `operation/production/events/execution/${eventId}/terminate`,
        data
      ),
    {
      ...config,
      onSuccess(data, vars, conext) {
        config?.onSuccess?.(data, vars, conext)
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          eventId,
        ])
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          { scope: "from_event_app" },
        ])
      },
      onMutate: async (data) => {
        await queryClient.cancelQueries({
          queryKey: [
            "operation",
            "production",
            "events",
            { scope: "from_event_app" },
          ],
        })

        const previousEvents = queryClient.getQueryData([
          "operation",
          "production",
          "events",
          { scope: "from_event_app" },
        ])

        queryClient.setQueryData(
          ["operation", "production", "events", { scope: "from_event_app" }],
          (old?: IEvent[]) => old?.filter((e) => e.id !== eventId)
        )

        return { previousEvents }
      },
      onError: (err, newData, context) => {
        queryClient.setQueryData(
          ["operation", "production", "events", { scope: "from_event_app" }],
          (context as any).previousEvents
        )
      },
      // Always refetch after error or success:
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [
            "operation",
            "production",
            "events",
            { scope: "from_event_app" },
          ],
        })
      },
    }
  )
}

export function useStartEvent(
  config?: UseMutationOptions<unknown, unknown, string, unknown>
) {
  const queryClient = useQueryClient()
  return useMutation(
    (id) => api.post(`operation/production/events/execution/${id}/start`),
    {
      ...config,
      onSuccess() {
        queryClient.invalidateQueries(["operation", "production", "events"])
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          "execution",
        ])
      },
    }
  )
}
